import { FormControl, Grid, InputAdornment, TextField, Typography } from '@material-ui/core';
import { equals } from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

import { NumberRange } from '../../../generated/model/models';
import { useOnChange } from '../../../hooks/useOnChange';
import InputDiscreteRangeSlider, {
  DISCRETE_RANGE_MARKS,
  DiscreteRangeEnum,
} from '../DiscreteRangeSlider';

interface OwnProps {
  onChange: (values: NumberRange) => void;
  defaultValue?: NumberRange;
  isPercentage: boolean;
}

const equalRangeState = [DiscreteRangeEnum.Equal, DiscreteRangeEnum.Equal];
const defaultRangeState = [DiscreteRangeEnum.Greater, DiscreteRangeEnum.PlusInfinity];

export default function InputNumberRangeSlider({ onChange, defaultValue, isPercentage }: OwnProps) {
  const [range, setRange] = useState<DiscreteRangeEnum[]>(getRangeFromState(defaultValue));
  const onComponentChanged = useCallback((state) => onChange(state), [onChange]);
  const [state, setState] = useState<NumberRange>(defaultValue ?? {});
  const isEqualMode = equals(equalRangeState, range);
  const [inputMinState, setInputMinState] = useState<number | undefined>(state.from);
  const [inputMaxState, setInputMaxState] = useState<number | undefined>(state.to);
  const TextInput = useMemo(
    () => (props: any) => (
      <TextField
        margin="normal"
        variant="outlined"
        fullWidth
        {...props}
        InputProps={
          isPercentage
            ? {
                startAdornment: (
                  <React.Fragment>
                    <InputAdornment position={'start'}>
                      {getInputPlaceholder(range[props.InputProps?.inputCountForRange])}
                    </InputAdornment>
                  </React.Fragment>
                ),
                endAdornment: (
                  <React.Fragment>
                    <InputAdornment position={'end'}>%</InputAdornment>
                  </React.Fragment>
                ),
              }
            : {
                startAdornment: (
                  <React.Fragment>
                    <InputAdornment position={'start'}>
                      {getInputPlaceholder(range[props.InputProps?.inputCountForRange])}
                    </InputAdornment>
                  </React.Fragment>
                ),
              }
        }
      />
    ),
    [isPercentage, range],
  );

  useOnChange(onComponentChanged, state);

  useEffect(() => {
    if (inputMinState !== state.from) {
      setInputMinState(typeof state.from !== 'undefined' ? state.from : (null as any));
    }
    if (inputMaxState !== state.to) {
      setInputMaxState(typeof state.to !== 'undefined' ? state.to : (null as any));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (!equals(defaultValue, state)) {
      setState(defaultValue ?? {});
      setRange(getRangeFromState(defaultValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  return (
    <>
      <Grid container direction="column">
        {!isEqualMode ? (
          <Grid container direction="row" justify="space-between" alignItems="center">
            <Grid item xs={5}>
              <NumberFormat
                value={inputMinState}
                disabled={range[0] === DiscreteRangeEnum.MinusInfinity}
                onValueChange={({ floatValue }) => setInputMinState(floatValue)}
                onBlur={() => setState({ ...state, from: inputMinState })}
                customInput={TextInput}
                InputProps={{
                  inputCountForRange: 0,
                }}
              />
            </Grid>
            <Grid>
              <FormControl margin="normal">
                <Typography>-</Typography>
              </FormControl>
            </Grid>
            <Grid item xs={5}>
              <NumberFormat
                value={inputMaxState}
                disabled={range[1] === DiscreteRangeEnum.PlusInfinity}
                onValueChange={({ floatValue }) => setInputMaxState(floatValue)}
                onBlur={() => setState({ ...state, to: inputMaxState })}
                customInput={TextInput}
                InputProps={{
                  inputCountForRange: 1,
                }}
              />
            </Grid>
          </Grid>
        ) : (
          <NumberFormat
            value={inputMaxState}
            disabled={range[1] === DiscreteRangeEnum.PlusInfinity}
            onValueChange={({ floatValue }) => {
              setInputMinState(floatValue);
              setInputMaxState(floatValue);
            }}
            onBlur={() => setState({ ...state, from: inputMaxState, to: inputMaxState })}
            customInput={TextInput}
            InputProps={{
              inputCountForRange: 0,
            }}
          />
        )}
        <InputDiscreteRangeSlider onChange={handleRangeSliderChange} defaultValue={range} />
      </Grid>
    </>
  );

  function handleRangeSliderChange(nextRange: number[]) {
    let nextState = { ...state };

    if (nextRange[0] !== range[0]) {
      if (nextRange[0] === DiscreteRangeEnum.Equal) {
        nextRange[1] = DiscreteRangeEnum.Equal;
        nextState = { ...nextState, to: state.from };
      } else if (range[1] === DiscreteRangeEnum.Equal) {
        nextRange[1] = DiscreteRangeEnum.PlusInfinity;
      }
    } else if (nextRange[1] !== range[1]) {
      if (nextRange[1] === DiscreteRangeEnum.Equal) {
        nextRange[0] = DiscreteRangeEnum.Equal;
        nextState = { ...nextState, from: state.to };
      } else if (range[0] === DiscreteRangeEnum.Equal) {
        nextRange[0] = DiscreteRangeEnum.MinusInfinity;
      }
    }

    setRange(nextRange);
    setState(getStateByRange(nextRange, nextState));
  }

  function getInputPlaceholder(rangeValue: DiscreteRangeEnum): string {
    const label =
      DISCRETE_RANGE_MARKS.find((mark) => mark.value === rangeValue)?.label ??
      DISCRETE_RANGE_MARKS[0].label;

    return label;
  }

  function getRangeFromState(value: NumberRange | undefined) {
    return value ? [getParsedRangeStart(value), getParsedRangeEnd(value)] : defaultRangeState;
  }

  function getParsedRangeStart(value: NumberRange): DiscreteRangeEnum {
    if (
      value.fromInclusive &&
      value.fromInclusive === value.toInclusive &&
      value.from === value.to
    ) {
      return DiscreteRangeEnum.Equal;
    } else if (value.fromInclusive) {
      return DiscreteRangeEnum.GreaterOrEqual;
    } else {
      return typeof value.from !== 'undefined' ? DiscreteRangeEnum.Greater : defaultRangeState[0];
    }
  }

  function getParsedRangeEnd(value: NumberRange) {
    if (value.toInclusive && value.toInclusive === value.fromInclusive && value.from === value.to) {
      return DiscreteRangeEnum.Equal;
    } else if (value.toInclusive) {
      return DiscreteRangeEnum.LowerOrEqual;
    } else {
      return typeof value.to !== 'undefined' ? DiscreteRangeEnum.Lower : defaultRangeState[1];
    }
  }

  function getStateByRange(
    rangeMarks: DiscreteRangeEnum[],
    currentState: NumberRange,
  ): NumberRange {
    let nextState: NumberRange = {
      from: undefined,
      fromInclusive: false,
      to: undefined,
      toInclusive: false,
    };

    switch (rangeMarks[0]) {
      case DiscreteRangeEnum.MinusInfinity:
        nextState = { ...nextState, from: undefined, fromInclusive: false };
        break;
      case DiscreteRangeEnum.GreaterOrEqual:
        nextState = { ...nextState, from: currentState.from, fromInclusive: true };
        break;
      case DiscreteRangeEnum.Greater:
        nextState = { ...nextState, from: currentState.from, fromInclusive: false };
        break;
      case DiscreteRangeEnum.Equal:
        nextState = {
          ...nextState,
          from: currentState.from,
          fromInclusive: true,
          to: currentState.to,
          toInclusive: true,
        };
        break;
      case DiscreteRangeEnum.Lower:
        nextState = { ...nextState, from: currentState.from, fromInclusive: false };
        break;
      case DiscreteRangeEnum.LowerOrEqual:
        nextState = { ...nextState, from: currentState.from, fromInclusive: true };
        break;
      case DiscreteRangeEnum.PlusInfinity:
        nextState = { ...nextState, from: undefined, fromInclusive: false };
        break;
    }

    switch (rangeMarks[1]) {
      case DiscreteRangeEnum.MinusInfinity:
        nextState = { ...nextState, to: undefined, toInclusive: false };
        break;
      case DiscreteRangeEnum.GreaterOrEqual:
        nextState = { ...nextState, to: currentState.to, toInclusive: true };
        break;
      case DiscreteRangeEnum.Greater:
        nextState = { ...nextState, to: currentState.to, toInclusive: false };
        break;
      case DiscreteRangeEnum.Equal:
        nextState = {
          ...nextState,
          from: currentState.from,
          fromInclusive: true,
          to: currentState.to,
          toInclusive: true,
        };
        break;
      case DiscreteRangeEnum.Lower:
        nextState = { ...nextState, to: currentState.to, toInclusive: false };
        break;
      case DiscreteRangeEnum.LowerOrEqual:
        nextState = { ...nextState, to: currentState.to, toInclusive: true };
        break;
      case DiscreteRangeEnum.PlusInfinity:
        nextState = { ...nextState, to: undefined, toInclusive: false };
        break;
    }

    return nextState;
  }
}
